/**
 * Authentication Service
 * Manages authentication state and token lifecycle
 * Agnostic to storage mechanism (localStorage vs cookies)
 */

import type {
  AuthStorageProvider,
  AuthTokens,
  AuthUser,
} from "./auth.interface";
import { LocalStorageAuthProvider } from "./providers/localStorage.provider";
import { CookieAuthProvider } from "./providers/cookie.provider";
import { featureFlags } from "@/services/features/feature-flags.service";

export class AuthService {
  private provider: AuthStorageProvider;
  private tokenRefreshTimer?: number;
  private readonly TOKEN_REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes

  constructor(provider?: AuthStorageProvider) {
    // Use feature flag to determine provider
    if (!provider) {
      if (featureFlags.isEnabled("USE_SECURE_COOKIE_AUTH")) {
        console.info("🔐 Using secure cookie authentication");
        this.provider = new CookieAuthProvider();
      } else {
        console.info("📦 Using localStorage authentication");
        this.provider = new LocalStorageAuthProvider();
      }
    } else {
      this.provider = provider;
    }
  }

  /**
   * Store authentication data after successful login
   */
  async setAuth(
    user: AuthUser,
    tokens: {
      accessToken: string;
      refreshToken?: string;
      expiresIn: number; // seconds
      refreshExpiresIn?: number; // seconds
    },
  ): Promise<void> {
    const now = Date.now();
    const authTokens: AuthTokens = {
      accessToken: tokens.accessToken,
      expiresAt: now + tokens.expiresIn * 1000,
      refreshToken: tokens.refreshToken,
      refreshExpiresAt: tokens.refreshExpiresIn
        ? now + tokens.refreshExpiresIn * 1000
        : undefined,
    };

    await Promise.all([
      this.provider.setTokens(authTokens),
      this.provider.setUser(user),
    ]);

    // Schedule token refresh
    this.scheduleTokenRefresh(authTokens);
  }

  /**
   * Get current access token
   */
  async getAccessToken(): Promise<string | null> {
    const tokens = await this.provider.getTokens();
    if (!tokens) return null;

    // Check if token is expired
    if (tokens.expiresAt <= Date.now()) {
      // Try to refresh if we have a refresh token
      if (
        tokens.refreshToken &&
        tokens.refreshExpiresAt &&
        tokens.refreshExpiresAt > Date.now()
      ) {
        // Trigger refresh (handled by auth store)
        return null;
      }
      // Both tokens expired, clear auth
      await this.clearAuth();
      return null;
    }

    return tokens.accessToken;
  }

  /**
   * Get current user
   */
  async getUser(): Promise<AuthUser | null> {
    return this.provider.getUser();
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const token = await this.getAccessToken();
    const user = await this.getUser();
    return !!(token && user);
  }

  /**
   * Clear all authentication data
   */
  async clearAuth(): Promise<void> {
    this.clearTokenRefreshTimer();
    await Promise.all([this.provider.clearTokens(), this.provider.clearUser()]);
  }

  /**
   * Update tokens after refresh
   */
  async updateTokens(tokens: {
    accessToken: string;
    refreshToken?: string;
    expiresIn: number;
    refreshExpiresIn?: number;
  }): Promise<void> {
    const now = Date.now();
    const authTokens: AuthTokens = {
      accessToken: tokens.accessToken,
      expiresAt: now + tokens.expiresIn * 1000,
      refreshToken: tokens.refreshToken,
      refreshExpiresAt: tokens.refreshExpiresIn
        ? now + tokens.refreshExpiresIn * 1000
        : undefined,
    };

    await this.provider.setTokens(authTokens);
    this.scheduleTokenRefresh(authTokens);
  }

  /**
   * Get refresh token if available
   */
  async getRefreshToken(): Promise<string | null> {
    const tokens = await this.provider.getTokens();
    if (!tokens || !tokens.refreshToken) return null;

    // Check if refresh token is expired
    if (tokens.refreshExpiresAt && tokens.refreshExpiresAt <= Date.now()) {
      return null;
    }

    return tokens.refreshToken;
  }

  /**
   * Set remember me preference
   */
  async setRememberMe(value: boolean): Promise<void> {
    return this.provider.setRememberMe(value);
  }

  /**
   * Get remember me preference
   */
  async getRememberMe(): Promise<boolean> {
    return this.provider.getRememberMe();
  }

  /**
   * Schedule automatic token refresh
   */
  private scheduleTokenRefresh(tokens: AuthTokens): void {
    this.clearTokenRefreshTimer();

    const timeUntilExpiry = tokens.expiresAt - Date.now();
    const refreshTime = timeUntilExpiry - this.TOKEN_REFRESH_BUFFER;

    if (refreshTime > 0 && tokens.refreshToken) {
      this.tokenRefreshTimer = window.setTimeout(() => {
        // Trigger refresh through store or API client
        window.dispatchEvent(new CustomEvent("auth:token-refresh-needed"));
      }, refreshTime);
    }
  }

  /**
   * Clear token refresh timer
   */
  private clearTokenRefreshTimer(): void {
    if (this.tokenRefreshTimer) {
      window.clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = undefined;
    }
  }

  /**
   * Initialize auth state from storage
   */
  async initialize(): Promise<void> {
    const tokens = await this.provider.getTokens();
    if (tokens) {
      this.scheduleTokenRefresh(tokens);
    }
  }
}

// Singleton instance
export const authService = new AuthService();
